import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Plus,
  Search, 
  Eye, 
  FileEdit, 
  Trash2,
  CheckCircle2,
  ChevronLeft, 
  ChevronRight, 
  ChevronDown,
  BookOpen,
  SlidersHorizontal
} from "lucide-react";
import { apiRequest } from "../services/api";



export default function BlogManagePage() {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusCounts, setStatusCounts] = useState({ all: 0, published: 0, unpublished: 0 });
  const [filter, setFilter] = useState("all"); // 'all' | 'published' | 'unpublished'
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("lastModified"); // 'title' | 'views' | 'lastModified'
  const [sortOrder, setSortOrder] = useState("desc"); // 'asc' | 'desc'
  const [currentPage, setCurrentPage] = useState(1);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const categoryDropdownRef = useRef(null);
  const PER_PAGE = 6;

  // Close dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(e.target)) {
        setIsCategoryDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  // Fetch all categories dynamically
  useEffect(() => {
    let active = true;
    apiRequest("/categories")
      .then((json) => {
        if (!active) return;
        if (json.status && Array.isArray(json.data)) {
          setCategories(json.data);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch categories:", err);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    const fetchBlogs = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await apiRequest(`/blog/posts?status=${filter}&limit=20`);
        if (!active) return;
        const items = response.data?.items || [];
        const mapped = items.map((blog) => ({
          id: blog.id || blog.slug,
          title: blog.title,
          author: typeof blog.author === "string" ? blog.author : (blog.author?.name || "Zytama Editorial"),
          category: typeof blog.category === "object" ? (blog.category?.categoryName || "General") : (blog.category || "General"),
          status: blog.status || (blog.date ? "published" : "unpublished"),
          lastModified: blog.lastModified || (blog.date ? new Date(blog.date).toISOString().split("T")[0] : "2026-06-08"),
          imageUrl: blog.imageUrl || blog.cover_image_url || blog.image || "https://images.unsplash.com/photo-1507238691740-187a5b1d37b8?auto=format&fit=crop&w=150&h=150&q=80",
          body: blog.body || "",
          meta_description: blog.meta_description || "",
        }));
        setBlogs(mapped);

        // If we fetched "all", update the status counts
        if (filter === "all") {
          const publishedCount = mapped.filter((b) => b.status === "published").length;
          const unpublishedCount = mapped.filter((b) => b.status === "unpublished" || b.status === "draft").length;
          setStatusCounts({
            all: mapped.length,
            published: publishedCount,
            unpublished: unpublishedCount,
          });
        }
      } catch (err) {
        if (!active) return;
        setError(err.message || "Failed to load blogs");
      } finally {
        if (active) setIsLoading(false);
      }
    };

    fetchBlogs();
    return () => {
      active = false;
    };
  }, [filter]);



  const handleToggleStatus = async (blog) => {
    const originalBlogs = blogs;
    // Optimistic update
    const newStatus = blog.status === "published" ? "unpublished" : "published";
    setBlogs((prev) =>
      prev.map((b) => (b.id === blog.id ? { ...b, status: newStatus } : b))
    );
    setStatusCounts((prev) => ({
      all: prev.all,
      published: blog.status === "published" ? Math.max(0, prev.published - 1) : prev.published + 1,
      unpublished: blog.status === "published" ? prev.unpublished + 1 : Math.max(0, prev.unpublished - 1),
    }));
    try {
      const json = await apiRequest(`/admin/blog/posts/${blog.id}/toggle-status`, {
        method: "PUT",
      });
      if (json.success && json.data?.status) {
        setBlogs((prev) =>
          prev.map((b) => (b.id === blog.id ? { ...b, status: json.data.status } : b))
        );
      }
    } catch (err) {
      // Revert on failure
      setBlogs(originalBlogs);
      alert(err.message || "Failed to toggle blog status.");
    }
  };

  // Filter and Sort logs logic
  const filteredBlogs = useMemo(() => {
    return blogs
      .filter((b) => {
        // Status filter
        if (filter !== "all") {
          if (filter === "unpublished") {
            if (b.status !== "unpublished" && b.status !== "draft") return false;
          } else if (b.status !== filter) {
            return false;
          }
        }
        
        // Category filter
        if (selectedCategory !== "all") {
          if (b.category.toLowerCase() !== selectedCategory.toLowerCase()) return false;
        }
        
        // Search filter
        const query = search.toLowerCase();
        return (
          b.title.toLowerCase().includes(query) ||
          b.author.toLowerCase().includes(query) ||
          b.category.toLowerCase().includes(query)
        );
      })
      .sort((a, b) => {
        let compare = 0;
        if (sortField === "views") {
          compare = a.views - b.views;
        } else if (sortField === "title") {
          compare = a.title.localeCompare(b.title);
        } else {
          // date
          compare = new Date(a.lastModified) - new Date(b.lastModified);
        }
        return sortOrder === "asc" ? compare : -compare;
      });
  }, [blogs, filter, selectedCategory, search, sortField, sortOrder]);

  const totalPages = Math.ceil(filteredBlogs.length / PER_PAGE);
  const pagedBlogs = useMemo(() => {
    const start = (currentPage - 1) * PER_PAGE;
    return filteredBlogs.slice(start, start + PER_PAGE);
  }, [filteredBlogs, currentPage]);



  const getExcerpt = (blog) => {
    if (blog.meta_description) return blog.meta_description;
    if (blog.body) {
      const stripped = blog.body.replace(/<[^>]+>/g, "").trim();
      return stripped.length > 120 ? stripped.substring(0, 120) + "..." : stripped;
    }
    return "No description available for this post. Click view to read more.";
  };

  return (
    <div className="p-1 md:p-4 animate-fadeIn">
      {/* Search & Filter Toolbar */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        {/* Custom Category Dropdown */}
        <div className="relative shrink-0" ref={categoryDropdownRef}>
          <button
            type="button"
            onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
            className="flex items-center justify-between gap-3 px-4 py-2.5 bg-white border border-gray-200 rounded-[14px] text-[15px] font-bold text-gray-800 hover:bg-gray-50 transition-all cursor-pointer min-w-[170px]"
          >
            <span className="truncate">
              {selectedCategory === "all" ? "All Categories" : selectedCategory}
            </span>
            <ChevronDown className={`text-gray-500 transition-transform duration-200 shrink-0 ${isCategoryDropdownOpen ? "rotate-180" : ""}`} size={16} />
          </button>

          {isCategoryDropdownOpen && (
            <div className="absolute top-full left-0 mt-2 w-[260px] bg-white border border-gray-200 rounded-[22px] shadow-[0px_12px_32px_rgba(0,0,0,0.06)] overflow-hidden z-50 py-0">
              {/* Option: All */}
              <div
                onClick={() => {
                  setSelectedCategory("all");
                  setCurrentPage(1);
                  setIsCategoryDropdownOpen(false);
                }}
                className={`flex items-center justify-between px-6 py-4 hover:bg-slate-50/75 cursor-pointer text-[15px] font-medium transition-colors border-b border-gray-100 ${
                  selectedCategory === "all" ? "text-[#0d5f66] font-semibold" : "text-[#111111]"
                }`}
              >
                <span>All</span>
                <span className="text-[#b0b0b0] font-normal">{String(blogs.length).padStart(2, "0")}</span>
              </div>

              {/* Dynamic categories options */}
              {categories.map((cat, idx) => {
                const categoryName = cat.categoryName;
                const count = blogs.filter((b) => b.category.toLowerCase() === categoryName.toLowerCase()).length;
                const isSelected = selectedCategory.toLowerCase() === categoryName.toLowerCase();
                const isLast = idx === categories.length - 1;
                return (
                  <div
                    key={cat.id || cat.categoryName}
                    onClick={() => {
                      setSelectedCategory(categoryName);
                      setCurrentPage(1);
                      setIsCategoryDropdownOpen(false);
                    }}
                    className={`flex items-center justify-between px-6 py-4 hover:bg-slate-50/75 cursor-pointer text-[15px] font-medium transition-colors ${
                      isLast ? "" : "border-b border-gray-100"
                    } ${
                      isSelected ? "text-[#0d5f66] font-semibold" : "text-[#111111]"
                    }`}
                  >
                    <span>{categoryName}</span>
                    <span className="text-[#b0b0b0] font-normal">{String(count).padStart(2, "0")}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Status Filter Tabs */}
        <div className="bg-white border border-gray-200 rounded-[14px] p-1.5 flex items-center gap-1 overflow-x-auto max-w-full shrink [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          {[
            { id: "all", label: "All Posts" },
            { id: "published", label: "Published" },
            { id: "unpublished", label: "Drafts" },
            // { id: "scheduled", label: "Scheduled" }
          ].map((tab) => {
            const active = filter === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setFilter(tab.id);
                  setCurrentPage(1);
                }}
                className={`px-4 py-2 rounded-[10px] text-[14px] font-bold transition-all whitespace-nowrap ${
                  active 
                    ? "bg-[#0d5f66] text-white shadow-sm"
                    : "text-[#5E5E5E] hover:bg-gray-50 bg-transparent"
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Search Input */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          <input
            type="text"
            placeholder="Search posts..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-[14px] text-[14px] focus:outline-none focus:border-[#0d5f66] transition-all bg-white placeholder-gray-400 text-gray-800"
          />
        </div>

        {/* Settings Icon */}
        {/* <button className="p-3 border border-gray-200 rounded-[14px] bg-white text-gray-500 hover:bg-gray-50 transition-all active:scale-95 flex items-center justify-center shrink-0">
          <SlidersHorizontal size={16} />
        </button> */}
      </div>

      {/* Create Post Action Row (Next Line) */}
      <div className="flex justify-between items-center mb-6">
        <button
          onClick={() => navigate("/blogmanage/new")}
          className="bg-[#0d5f66] hover:bg-[#094e5c] text-white font-bold px-5 py-2.5 rounded-[14px] shadow-sm transition-all flex items-center gap-2 text-[14px] active:scale-95"
        >
          <Plus size={16} />
          Create Post
        </button>
      </div>

      {/* Main Grid View */}
      {isLoading ? (
        <div className="bg-white border border-gray-200 rounded-[24px] py-24 text-center shadow-[0px_4px_32px_0px_#0000000A]">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0d5f66]"></div>
            <p className="font-bold text-gray-700">Loading blog posts...</p>
          </div>
        </div>
      ) : error ? (
        <div className="bg-white border border-gray-200 rounded-[24px] py-24 text-center shadow-[0px_4px_32px_0px_#0000000A]">
          <div className="flex flex-col items-center gap-2 text-rose-500">
            <p className="font-bold text-lg">Error loading blogs</p>
            <p className="text-sm">{error}</p>
          </div>
        </div>
      ) : pagedBlogs.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-[24px] py-24 text-center shadow-[0px_4px_32px_0px_#0000000A]">
          <div className="flex flex-col items-center gap-3 text-gray-400">
            <BookOpen size={48} className="text-gray-300" />
            <p className="font-bold text-lg text-gray-750">No blog posts found</p>
            <p className="text-sm">Try adjusting your filters or search terms</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pagedBlogs.map((blog) => (
            <div 
              key={blog.id} 
              className="bg-white border border-gray-200 rounded-[24px] overflow-hidden shadow-[0px_4px_32px_0px_#0000000A] flex flex-col group hover:shadow-md transition-all duration-300"
            >
              {/* Card Image */}
              <div className="relative aspect-[16/10] w-full overflow-hidden bg-gray-50 shrink-0">
                <img
                  alt="Post Cover"
                  className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                  src={blog.imageUrl}
                />
                {/* Status Badge */}
                <div className="absolute top-4 right-4">
                  {blog.status === "published" ? (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/95 backdrop-blur-sm shadow-sm rounded-full text-xs font-extrabold text-emerald-700 border border-emerald-100">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      Published
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/95 backdrop-blur-sm shadow-sm rounded-full text-xs font-extrabold text-gray-500 border border-gray-100">
                      <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                      Unpublished
                    </span>
                  )}
                </div>
              </div>

              {/* Card Body */}
              <div className="p-6 flex-1 flex flex-col justify-between gap-4">
                <div className="space-y-2">
                  {/* Title */}
                  <h3
                    onClick={() => navigate(`/blogmanage/${blog.id}?mode=view`)}
                    className="text-[17px] font-extrabold text-black line-clamp-2 hover:text-[#0d5f66] cursor-pointer transition-colors leading-snug"
                  >
                    {blog.title}
                  </h3>
                  {/* Excerpt */}
                  <p className="text-gray-500 text-[14px] leading-relaxed line-clamp-3">
                    {getExcerpt(blog)}
                  </p>
                </div>

                {/* Card Footer */}
                <div className="pt-4 border-t border-gray-200 flex items-center justify-between shrink-0">
                  <div className="flex items-center gap-3">
                    {/* Avatar */}
                    <div className="w-[38px] h-[38px] rounded-full bg-[#0d5f66]/10 text-[#0d5f66] flex items-center justify-center text-[13px] font-extrabold border border-[#0d5f66]/10 overflow-hidden shrink-0">
                      <img 
                        src={`https://ui-avatars.com/api/?name=${encodeURIComponent(blog.author)}&background=0d5f66&color=fff&bold=true&size=100`} 
                        alt={blog.author} 
                        className="w-full h-full object-cover" 
                      />
                    </div>
                    <div>
                      <div className="text-[13px] font-bold text-black leading-tight">
                        {blog.author}
                      </div>
                      <div className="text-[11px] text-gray-400 mt-0.5">
                        {new Date(blog.lastModified).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric"
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => navigate(`/blogmanage/${blog.id}?mode=view`)}
                      className="p-2 text-gray-400 hover:text-[#0d5f66] hover:bg-gray-50 rounded-xl transition-all"
                      title="View Details"
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      onClick={() => navigate(`/blogmanage/${blog.id}?mode=edit`)}
                      className="p-2 text-gray-400 hover:text-[#0d5f66] hover:bg-gray-50 rounded-xl transition-all"
                      title="Edit Post"
                    >
                      <FileEdit size={16} />
                    </button>
                    {blog.status === "published" ? (
                      <button
                        onClick={() => handleToggleStatus(blog)}
                        className="p-2 text-rose-600 hover:bg-rose-50 rounded-xl transition-all"
                        title="Unpublish Post"
                      >
                        <Trash2 size={16} />
                      </button>
                    ) : (
                      <button
                        onClick={() => handleToggleStatus(blog)}
                        className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-xl transition-all"
                        title="Publish Post"
                      >
                        <CheckCircle2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination Footer */}
      {totalPages > 1 && (
        <div className="bg-white border border-gray-200 rounded-[24px] px-6 py-4 mt-8 flex items-center justify-between flex-col sm:flex-row gap-4 shadow-[0px_4px_32px_0px_#0000000A]">
          <p className="text-[13px] text-gray-500 font-medium">
            Showing{" "}
            <span className="font-bold text-black">
              {(currentPage - 1) * PER_PAGE + 1}
            </span>
            -
            <span className="font-bold text-black">
              {Math.min(currentPage * PER_PAGE, filteredBlogs.length)}
            </span>{" "}
            of <span className="font-bold text-black">{filteredBlogs.length}</span>{" "}
            posts
          </p>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="w-[34px] h-[34px] border border-gray-200 rounded-xl flex items-center justify-center bg-white hover:bg-gray-50 transition-all text-gray-500 disabled:opacity-40 disabled:hover:bg-transparent"
            >
              <ChevronLeft size={16} />
            </button>
            
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentPage(i + 1)}
                className={`w-[34px] h-[34px] rounded-xl flex items-center justify-center font-bold text-[13px] transition-all ${
                  currentPage === i + 1
                    ? "bg-[#0d5f66] text-white shadow-sm"
                    : "border border-gray-200 bg-white hover:bg-gray-50 text-[#5E5E5E]"
                }`}
              >
                {i + 1}
              </button>
            ))}

            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="w-[34px] h-[34px] border border-gray-200 rounded-xl flex items-center justify-center bg-white hover:bg-gray-50 transition-all text-gray-500 disabled:opacity-40 disabled:hover:bg-transparent"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
